import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { FileInsTargetRequest } from "./fileInsSlice";

export interface FileUploadState {
  filename: string;
  preview: string;
  state: LoadStateInfo;
}

export interface FilesUploadState {
  files: Record<string, FileUploadState>;
}

const initialState: FilesUploadState = {
  files: {},
};

export interface FileUploadRequest {
  file: FileUploadState;
  raw: File;
  target: FileInsTargetRequest;
}

export const fileUpload = createAsyncThunk(
  "fileUpload",
  async (param: FileUploadRequest, thunkAPI) => {
    console.debug(`upload file ${param.file.filename}`);

    let queryParams = `filename=${encodeURIComponent(param.file.filename)}`;
    queryParams += `&schoolId=${param.target.schoolId}`;
    queryParams += `&conversationId=${param.target.conversationId}`;
    queryParams += `&partyId=${param.target.partyId}`;

    const response = await fetch(`${getBackendUrl()}/api/file?${queryParams}`, {
      method: "POST",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": `${param.raw.type}`,
      },
      body: param.raw
    });

    if(response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse( response ));
    }
  }
);


export const filesUploadSlice = createSlice({
  name: "filesUploadSlice",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    add: (state, action: PayloadAction<{filename: string, preview: string}>) => {
      if(!state.files[action.payload.filename]) {
        state.files[action.payload.filename] = {
          filename: action.payload.filename,
          preview: action.payload.preview, 
          state: notStarted()
        };
      }
    },
    retry: (state, action: PayloadAction<string>) => {
      state.files[action.payload].state = notStarted();
    },
    remove: (state, action: PayloadAction<string>) => {
      delete state.files[action.payload];
    },
    clean: (state) => {
      state.files = {};
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fileUpload.pending, (state, action) => {
        state.files[action.meta.arg.file.filename].state =  loading();
      })
      .addCase(fileUpload.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.files[action.meta.arg.file.filename].state =  loaded();
      })
      .addCase(fileUpload.rejected, (state, action) => {
        state.files[action.meta.arg.file.filename].state = error(action);
      });
    },
});

// export const { } = filesUploadSlice.actions;

export const selectFilesUpload = (state: RootState) => state.filesUpload;

export default filesUploadSlice.reducer;
